import React, { useCallback } from 'react';
import { Pressable } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

import { playerPathwayLevel1_3, playerPathwayLevel4_5 } from '@/assets/images';
import { Scroll, Stack } from '@/components/layout';
import { Image, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { CoachMaterialHeader } from '@/features/guardian-player/components/coach-material-header';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import { ensureOnline } from '@/libs/network/network-status';
import { colors, guardianColors } from '@/theme/colors';

const copy = STRINGS.guardianPlayer.coachMaterials;

const MATERIALS = [
  {
    id: 'levels-1-3',
    title: copy.levelOneToThree,
    description: copy.levelOneToThreeDescription,
    image: playerPathwayLevel1_3,
    url: 'https://viewer.joomag.com/player-pathway-level-1-3-march2024/0371503001697045141?short&',
  },
  {
    id: 'levels-4-5',
    title: copy.levelFourToFive,
    description: copy.levelFourToFiveDescription,
    image: playerPathwayLevel4_5,
    url: 'https://viewer.joomag.com/2026-player-pathway-level-4-5/0510542001772572274',
  },
] as const;

export function GuardianCoachMaterialsScreen() {
  const navigation = useGuardianStackNavigation();
  const { scale, isTablet } = useScaling();
  const styles = useStyles();

  const handleBack = useCallback(() => navigation.goBack(), [navigation]);
  const openMaterial = useCallback(
    async (url: string, title: string) => {
      await ensureOnline();
      navigation.navigate('GuardianCoachMaterialWebView', { url, title });
    },
    [navigation],
  );

  const content = (
    <>
      <CoachMaterialHeader title={copy.title} onBack={handleBack} />
      <Scroll showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
        <Stack width="100%" maxWidth={scale(776)} align="center" style={styles.content}>
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
            style={[styles.description, isTablet ? styles.descriptionTablet : null]}>
            {copy.description}
          </Text>
          <Stack width="100%" gap={scale({ phone: 16, tablet: 16 })}>
            {MATERIALS.map(material => (
              <Pressable
                key={material.id}
                accessibilityRole="button"
                accessibilityLabel={material.title}
                onPress={() => openMaterial(material.url, material.title)}
                style={({ pressed }) => [styles.card, pressed ? styles.pressed : null]}>
                <Image
                  source={material.image}
                  width={scale(60)}
                  height={scale(82)}
                  style={styles.thumbnail}
                />
                <Stack flex={1} align="flex-start" justify="space-between" alignSelf="stretch">
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                    style={styles.cardTitle}>
                    {material.title}
                  </Text>
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                    style={styles.meta}>
                    {isTablet ? material.description.tablet : material.description.phone}
                  </Text>
                </Stack>
                <SvgIcon name="IcChevronRight" size={scale(24)} color={guardianColors['6']} />
              </Pressable>
            ))}
          </Stack>
        </Stack>
      </Scroll>
    </>
  );

  if (isTablet)
    return (
      <Stack flex={1} backgroundColor={guardianColors['1']}>
        {content}
      </Stack>
    );

  return (
    <LinearGradient
      colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
      locations={[0.08, 0.47, 0.9]}
      useAngle
      angle={210.65}
      style={styles.gradient}>
      {content}
    </LinearGradient>
  );
}

const useStyles = createStyles(({ scale }) => ({
  gradient: { flex: 1 },
  scrollContent: { alignItems: 'center', paddingBottom: scale(24) },
  content: {
    paddingHorizontal: scale({ phone: 20, tablet: 0 }),
    paddingTop: scale({ phone: 12, tablet: 16 }),
  },
  description: { width: '100%', color: guardianColors['10'], marginBottom: scale(16) },
  descriptionTablet: { textAlign: 'center', marginBottom: scale(16) },
  card: {
    width: '100%',
    minHeight: scale(98),
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(12),
    padding: scale(8),
    borderRadius: scale(12),
    backgroundColor: colors['0'],
  },
  thumbnail: { borderRadius: scale(6) },
  cardTitle: { color: guardianColors['10'] },
  meta: { color: guardianColors['6'] },
  pressed: { opacity: 0.72 },
}));
